import { createIcon } from "./icons";

export type DocumentSwitcherPosition = "top-left" | "top-right" | "bottom-left" | "bottom-right";

export interface DocumentSwitcherItem {
  id: string;
  label: string;
}

export interface DocumentSwitcherState {
  documents: DocumentSwitcherItem[];
  currentDocumentId: string | undefined;
}

export interface DocumentSwitcherOptions {
  getState: () => DocumentSwitcherState;
  position?: DocumentSwitcherPosition;
  style?: Partial<CSSStyleDeclaration>;
  label?: string;
}

const POSITION_OFFSETS: Record<DocumentSwitcherPosition, Partial<CSSStyleDeclaration>> = {
  "top-left": { top: "10px", left: "10px" },
  "top-right": { top: "10px", right: "10px" },
  "bottom-left": { bottom: "10px", left: "10px" },
  "bottom-right": { bottom: "10px", right: "10px" },
};

export class DocumentSwitcher {
  private el: HTMLDivElement;
  private toggleButton: HTMLButtonElement;
  private toggleLabel: HTMLSpanElement;
  private listEl: HTMLUListElement;
  private open = false;
  private edgeProperty: "top" | "bottom";
  private resizeObserver: ResizeObserver;

  constructor(
    container: HTMLElement,
    private options: DocumentSwitcherOptions,
    private onSelect: (documentId: string) => void,
    onContainerResize: () => void,
  ) {
    const position = options.position ?? "top-right";
    const [vertical, horizontal] = position.split("-");

    this.edgeProperty = vertical === "top" ? "top" : "bottom";
    this.el = document.createElement("div");
    this.el.className = `toka-document-switcher toka-document-switcher--${vertical} toka-document-switcher--${horizontal}`;
    Object.assign(this.el.style, POSITION_OFFSETS[position], options.style);

    const label = options.label ?? "Changer de carte";
    this.toggleButton = document.createElement("button");
    this.toggleButton.type = "button";
    this.toggleButton.className = "toka-document-switcher__toggle";
    this.toggleButton.title = label;
    this.toggleButton.setAttribute("aria-label", label);
    this.toggleButton.setAttribute("aria-haspopup", "listbox");
    this.toggleButton.setAttribute("aria-expanded", "false");
    this.toggleLabel = document.createElement("span");
    this.toggleLabel.className = "toka-document-switcher__label";
    const chevron = createIcon("chevron-down");
    chevron.classList.add("toka-document-switcher__chevron");
    this.toggleButton.append(createIcon("layers"), this.toggleLabel, chevron);
    this.toggleButton.addEventListener("click", () => this.setOpen(!this.open));

    this.listEl = document.createElement("ul");
    this.listEl.className = "toka-document-switcher__list";
    this.listEl.setAttribute("role", "listbox");
    this.listEl.setAttribute("aria-label", label);

    this.el.append(this.toggleButton, this.listEl);
    container.appendChild(this.el);

    document.addEventListener("pointerdown", this.handleOutsidePointerDown);
    this.el.addEventListener("keydown", this.handleKeyDown);
    this.resizeObserver = new ResizeObserver(onContainerResize);
    this.resizeObserver.observe(container);
    this.refresh();
  }

  refresh(): void {
    const { documents, currentDocumentId } = this.options.getState();
    this.el.style.display = documents.length < 2 ? "none" : "";
    if (documents.length < 2) this.setOpen(false);
    this.toggleLabel.textContent = documents.find((d) => d.id === currentDocumentId)?.label ?? "";
    this.renderList(documents, currentDocumentId);
  }

  widthPx(): number {
    return this.el.offsetWidth;
  }

  setStackOffset(offsetPx: number): void {
    this.el.style[this.edgeProperty] = `${offsetPx}px`;
  }

  destroy(): void {
    document.removeEventListener("pointerdown", this.handleOutsidePointerDown);
    this.resizeObserver.disconnect();
    this.el.remove();
  }

  private setOpen(open: boolean): void {
    if (open) this.refresh();
    this.open = open;
    this.el.classList.toggle("toka-document-switcher--open", open);
    this.toggleButton.setAttribute("aria-expanded", String(open));
  }

  private renderList(documents: DocumentSwitcherItem[], currentDocumentId: string | undefined): void {
    this.listEl.replaceChildren(
      ...documents.map((item) => {
        const isCurrent = item.id === currentDocumentId;
        const option = document.createElement("li");
        option.setAttribute("role", "option");
        option.setAttribute("aria-selected", String(isCurrent));
        const button = document.createElement("button");
        button.type = "button";
        button.className = "toka-document-switcher__item";
        button.classList.toggle("toka-document-switcher__item--current", isCurrent);
        const text = document.createElement("span");
        text.className = "toka-document-switcher__item-label";
        text.textContent = item.label;
        button.appendChild(text);
        if (isCurrent) button.appendChild(createIcon("check"));
        button.addEventListener("click", () => {
          this.setOpen(false);
          if (!isCurrent) this.onSelect(item.id);
        });
        option.appendChild(button);
        return option;
      }),
    );
  }

  private handleOutsidePointerDown = (event: PointerEvent): void => {
    if (this.open && !this.el.contains(event.target as Node)) this.setOpen(false);
  };

  private handleKeyDown = (event: KeyboardEvent): void => {
    if (event.key !== "Escape" || !this.open) return;
    this.setOpen(false);
    this.toggleButton.focus();
  };
}
